import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_COMMITTEEREGULARINVITEE_LOAD_BY_ID,
    ACTION_COMMITTEEREGULARINVITEE_LOAD_BY_COMMITTEE_ID,
    ACTION_COMMITTEEREGULARINVITEE_LOAD_FAILURE,
    ACTION_COMMITTEEREGULARINVITEE_LOAD_SUCCESS,

    ACTION_COMMITTEEREGULARINVITEE_SAVE,
    ACTION_COMMITTEEREGULARINVITEE_SAVE_SUCCESS,
    ACTION_COMMITTEEREGULARINVITEE_SAVE_FAILURE,

    ACTION_COMMITTEEREGULARINVITEE_CREATE,
    ACTION_COMMITTEEREGULARINVITEE_CREATE_SUCCESS,
    ACTION_COMMITTEEREGULARINVITEE_CREATE_FAILURE,

    ACTION_COMMITTEEREGULARINVITEE_VALIDATE,
    ACTION_COMMITTEEREGULARINVITEE_VALIDATE_SUCCESS,
    ACTION_COMMITTEEREGULARINVITEE_VALIDATE_FAILURE,

    ACTION_COMMITTEEREGULARINVITEE_CLEAR,
    ACTION_COMMITTEEREGULARINVITEE_FILTER,

    CommitteeRegularInviteeAction
} from '../actions/committeeRegularInvitee';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ICommitteeRegularInviteeState {
    formData: Dtos.CommitteeRegularInvitee | Dtos.CommitteeRegularInvitee[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.CommitteeRegularInvitee[];
    formLength: number;
}

const initialState: ICommitteeRegularInviteeState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ICommitteeRegularInviteeState, _isLoading: boolean): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ICommitteeRegularInviteeState,
    formData: Dtos.CommitteeRegularInvitee | Dtos.CommitteeRegularInvitee[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ICommitteeRegularInviteeState, responseStatus: Dtos.ResponseStatus): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ICommitteeRegularInviteeState, formFilter: IFormFilter): ICommitteeRegularInviteeState {

    const items: Dtos.CommitteeRegularInvitee[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            (p.comment && p.comment.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
        )) : undefined;

    const newState: ICommitteeRegularInviteeState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ICommitteeRegularInviteeState, propertiesToClear?: string[]): ICommitteeRegularInviteeState {

    if (!propertiesToClear) {
        const newState: ICommitteeRegularInviteeState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ICommitteeRegularInviteeState, _isSaving: boolean): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ICommitteeRegularInviteeState,
    formData: Dtos.CommitteeRegularInvitee | Dtos.CommitteeRegularInvitee[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ICommitteeRegularInviteeState, responseStatus: Dtos.ResponseStatus): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ICommitteeRegularInviteeState, _isCreating: boolean): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ICommitteeRegularInviteeState,
    formData: Dtos.CommitteeRegularInvitee,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ICommitteeRegularInviteeState, responseStatus: Dtos.ResponseStatus): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ICommitteeRegularInviteeState, _isValidating: boolean): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ICommitteeRegularInviteeState,
    formData: Dtos.CommitteeRegularInvitee,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ICommitteeRegularInviteeState, responseStatus: Dtos.ResponseStatus): ICommitteeRegularInviteeState {
    const newState: ICommitteeRegularInviteeState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const CommitteeRegularInviteeReducer: Reducer<ICommitteeRegularInviteeState> = (state: ICommitteeRegularInviteeState = initialState, action: CommitteeRegularInviteeAction) => {
    switch (action.type) {
        case ACTION_COMMITTEEREGULARINVITEE_LOAD_BY_ID:
        case ACTION_COMMITTEEREGULARINVITEE_LOAD_BY_COMMITTEE_ID:
            return loading(state, true);
        case ACTION_COMMITTEEREGULARINVITEE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_COMMITTEEREGULARINVITEE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_COMMITTEEREGULARINVITEE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_COMMITTEEREGULARINVITEE_SAVE:
            return saving(state, true);
        case ACTION_COMMITTEEREGULARINVITEE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEREGULARINVITEE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_COMMITTEEREGULARINVITEE_CREATE:
            return creating(state, true);
        case ACTION_COMMITTEEREGULARINVITEE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_COMMITTEEREGULARINVITEE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_COMMITTEEREGULARINVITEE_VALIDATE:
            return validating(state, true);
        case ACTION_COMMITTEEREGULARINVITEE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEREGULARINVITEE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_COMMITTEEREGULARINVITEE_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default CommitteeRegularInviteeReducer;